import { useState, useEffect, useMemo } from 'react';
import { translationCenterApi } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent } from '../ui/card';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import {
    Search, AlertCircle, Check, Loader2, Filter, Layers, Lock,
} from 'lucide-react';

/**
 * Translation Center — Dynamic Content tab.
 * Table: Source | Item | Field | English | <selected lang>
 * All entries auto-discovered from registered sources. Inline edit
 * upserts through PUT /translation-center/entries. Empty value clears
 * the override so runtime falls back to English on the source row.
 */
export default function DynamicContentTab({ selectedLang, languageLabel, isAdmin }) {
    const [sources, setSources] = useState([]);
    const [sourceFilter, setSourceFilter] = useState('all');
    const [missingOnly, setMissingOnly] = useState(false);
    const [needsReviewOnly, setNeedsReviewOnly] = useState(false);
    const [search, setSearch] = useState('');
    const [entries, setEntries] = useState([]);
    const [totalMissing, setTotalMissing] = useState(0);
    const [totalNeedsReview, setTotalNeedsReview] = useState(0);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // {source, entity_id, field}
    const [editValue, setEditValue] = useState('');
    const [saving, setSaving] = useState(false);

    // Load registered sources once
    useEffect(() => {
        translationCenterApi.getSources()
            .then((r) => setSources(r.data.sources || []))
            .catch(() => toast.error('Failed to load translation sources'));
    }, []);

    // Load entries whenever language, source filter or review filter changes
    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const params = { lang: selectedLang };
                if (sourceFilter !== 'all') params.source = sourceFilter;
                if (missingOnly) params.missing_only = true;
                if (needsReviewOnly) params.needs_review_only = true;
                const res = await translationCenterApi.getEntries(params);
                setEntries(res.data.entries || []);
                setTotalMissing(res.data.total_missing || 0);
                setTotalNeedsReview(res.data.total_needs_review || 0);
            } catch (e) {
                toast.error('Failed to load dynamic content');
                setEntries([]);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [selectedLang, sourceFilter, missingOnly, needsReviewOnly]);

    const filtered = useMemo(() => {
        if (!search.trim()) return entries;
        const q = search.toLowerCase();
        return entries.filter((e) =>
            (e.item_label || '').toLowerCase().includes(q) ||
            (e.en || '').toLowerCase().includes(q) ||
            (e.value || '').toLowerCase().includes(q) ||
            (e.source_label || '').toLowerCase().includes(q) ||
            (e.field_label || '').toLowerCase().includes(q)
        );
    }, [entries, search]);

    const startEdit = (entry) => {
        setEditing({ source: entry.source, entity_id: entry.entity_id, field: entry.field });
        setEditValue(entry.value || '');
    };

    const cancelEdit = () => {
        setEditing(null);
        setEditValue('');
    };

    const isEditing = (entry) =>
        editing &&
        editing.source === entry.source &&
        editing.entity_id === entry.entity_id &&
        editing.field === entry.field;

    const save = async (entry) => {
        if (saving) return;
        setSaving(true);
        try {
            const trimmed = editValue.trim();
            await translationCenterApi.upsert({
                source: entry.source,
                entity_id: entry.entity_id,
                field: entry.field,
                lang: selectedLang,
                text: trimmed,
            });
            setEntries((prev) =>
                prev.map((e) =>
                    e.source === entry.source && e.entity_id === entry.entity_id && e.field === entry.field
                        ? {
                            ...e,
                            value: trimmed,
                            is_missing: !trimmed,
                            // Save re-snapshots the current English → drift is cleared.
                            needs_review: false,
                            en_at_translation: e.en,
                            updated_at: new Date().toISOString(),
                        }
                        : e,
                ),
            );
            setTotalMissing((n) => n + (entry.is_missing && trimmed ? -1 : !entry.is_missing && !trimmed ? 1 : 0));
            setTotalNeedsReview((n) => n + (entry.needs_review ? -1 : 0));
            setEditing(null);
            toast.success(trimmed ? 'Translation saved' : 'Cleared — falls back to English');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    const onKeyDown = (e, entry) => {
        if (e.key === 'Enter' && !e.shiftKey && entry.field_type !== 'long') {
            e.preventDefault();
            save(entry);
        } else if (e.key === 'Escape') {
            cancelEdit();
        }
    };

    return (
        <div className="space-y-4" data-testid="dynamic-content-tab">
            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[280px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                        placeholder="Search source, item, English or translation…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-10"
                        data-testid="dynamic-search"
                    />
                </div>
                <div className="flex items-center gap-2">
                    <Layers className="h-4 w-4 text-slate-500" />
                    <Select value={sourceFilter} onValueChange={setSourceFilter}>
                        <SelectTrigger className="w-[240px]" data-testid="dynamic-source-filter">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All sources</SelectItem>
                            {sources.map((s) => (
                                <SelectItem key={s.source} value={s.source}>{s.label}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div className="flex items-center gap-2 bg-white border border-border rounded-md px-3 py-2">
                    <Filter className="h-4 w-4 text-slate-400" />
                    <Label htmlFor="dyn-missing-only" className="text-sm text-slate-600 cursor-pointer">
                        Only missing
                    </Label>
                    <Switch
                        id="dyn-missing-only"
                        checked={missingOnly}
                        onCheckedChange={setMissingOnly}
                        data-testid="dynamic-missing-toggle"
                    />
                </div>
                <div className="flex items-center gap-2 bg-white border border-amber-200 rounded-md px-3 py-2">
                    <AlertCircle className="h-4 w-4 text-amber-500" />
                    <Label htmlFor="dyn-needs-review-only" className="text-sm text-amber-700 cursor-pointer">
                        Needs review{totalNeedsReview > 0 ? ` (${totalNeedsReview})` : ''}
                    </Label>
                    <Switch
                        id="dyn-needs-review-only"
                        checked={needsReviewOnly}
                        onCheckedChange={setNeedsReviewOnly}
                        data-testid="dynamic-needs-review-toggle"
                    />
                </div>
                <div className="text-xs text-slate-500 ml-auto">
                    {filtered.length} entries · {totalMissing} missing · {totalNeedsReview} needs review
                </div>
            </div>

            {/* Table */}
            <Card className="border-slate-300">
                <CardContent className="p-0">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="text-center py-12 text-slate-500">
                            <Layers className="h-10 w-10 mx-auto mb-3 text-slate-300" />
                            <p className="text-sm">
                                {missingOnly ? 'No missing dynamic content translations' : 'No translatable dynamic content matched your filters'}
                            </p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b border-border bg-background">
                                        <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider w-[14%]">
                                            Source
                                        </th>
                                        <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider w-[22%]">
                                            Item
                                        </th>
                                        <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider w-[14%]">
                                            Field
                                        </th>
                                        <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider w-[24%]">
                                            <div className="flex items-center gap-1.5">
                                                <Lock className="h-3 w-3 text-slate-400" /> English (Source)
                                            </div>
                                        </th>
                                        <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider w-[26%]">
                                            {languageLabel}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.map((entry, idx) => {
                                        const rowKey = `${entry.source}|${entry.entity_id}|${entry.field}`;
                                        const editingThis = isEditing(entry);
                                        return (
                                            <tr
                                                key={rowKey}
                                                className={`border-b border-slate-100 hover:bg-background ${idx % 2 === 0 ? 'bg-white' : 'bg-background/50'}`}
                                                data-testid={`dyn-row-${entry.source}-${entry.entity_id}-${entry.field}`}
                                            >
                                                <td className="py-3 px-4 align-top">
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-100">
                                                        {entry.source_label}
                                                    </span>
                                                </td>
                                                <td className="py-3 px-4 align-top">
                                                    <div className="text-sm font-medium text-slate-800 truncate max-w-[280px]" title={entry.item_label}>
                                                        {entry.item_label}
                                                    </div>
                                                    {entry.context && (
                                                        <div className="text-xs text-slate-400 mt-0.5 truncate max-w-[280px]" title={entry.context}>
                                                            {entry.context}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="py-3 px-4 align-top">
                                                    <div className="text-xs text-slate-600">{entry.field_label}</div>
                                                    <code className="text-[10px] text-slate-400">{entry.field}</code>
                                                </td>
                                                <td className="py-3 px-4 align-top">
                                                    <div className="text-sm text-slate-600 bg-slate-100/60 p-2 rounded border border-border whitespace-pre-wrap break-words">
                                                        {entry.en}
                                                    </div>
                                                </td>
                                                <td className="py-3 px-4 align-top">
                                                    {editingThis ? (
                                                        <div className="flex flex-col gap-2">
                                                            {entry.field_type === 'long' ? (
                                                                <Textarea
                                                                    value={editValue}
                                                                    onChange={(e) => setEditValue(e.target.value)}
                                                                    onKeyDown={(e) => onKeyDown(e, entry)}
                                                                    className="text-sm min-h-[80px]"
                                                                    autoFocus
                                                                    data-testid={`dyn-edit-${rowKey}`}
                                                                />
                                                            ) : (
                                                                <Input
                                                                    value={editValue}
                                                                    onChange={(e) => setEditValue(e.target.value)}
                                                                    onKeyDown={(e) => onKeyDown(e, entry)}
                                                                    className="text-sm"
                                                                    autoFocus
                                                                    data-testid={`dyn-edit-${rowKey}`}
                                                                />
                                                            )}
                                                            <div className="flex items-center gap-2">
                                                                <Button
                                                                    size="sm"
                                                                    onClick={() => save(entry)}
                                                                    disabled={saving || !isAdmin}
                                                                    data-testid={`dyn-save-${rowKey}`}
                                                                >
                                                                    {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Check className="h-3 w-3 mr-1" />Save</>}
                                                                </Button>
                                                                <Button size="sm" variant="ghost" onClick={cancelEdit}>Cancel</Button>
                                                                <span className="text-xs text-slate-400 ml-auto">
                                                                    {entry.field_type === 'long' ? 'Shift+Enter for newline' : 'Enter to save'} · Esc to cancel
                                                                </span>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div
                                                            className={`text-sm cursor-pointer p-2 rounded border min-h-[36px] whitespace-pre-wrap break-words ${entry.is_missing ? 'bg-amber-50 border-transparent' : entry.needs_review ? 'bg-amber-50/70 border-amber-200' : 'border-transparent hover:border-border'}`}
                                                            onClick={() => startEdit(entry)}
                                                            data-testid={`dyn-cell-${rowKey}`}
                                                        >
                                                            {entry.value ? (
                                                                <>
                                                                    {entry.needs_review && (
                                                                        <div className="mb-1">
                                                                            <span
                                                                                className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200"
                                                                                data-testid={`dyn-needs-review-badge-${rowKey}`}
                                                                            >
                                                                                <AlertCircle className="h-3 w-3" /> Needs review — source changed
                                                                            </span>
                                                                        </div>
                                                                    )}
                                                                    <div>{entry.value}</div>
                                                                    {entry.needs_review && entry.en_at_translation && (
                                                                        <div className="mt-1 text-[11px] text-amber-700/80">
                                                                            <span className="font-medium">Was:</span> {entry.en_at_translation}
                                                                        </div>
                                                                    )}
                                                                </>
                                                            ) : (
                                                                <span className="inline-flex items-center gap-1 text-amber-600 text-xs">
                                                                    <AlertCircle className="h-3 w-3" /> Missing — falls back to English
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
